/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { ContentType } from 'types/publishedAssets';
import { useGetContentTranslations } from 'utils/useGetContentTranslations';
import classNames from 'classnames';
import { Icon } from 'components/Atoms/Icon/Icon';
import { ContentContainer, SMALL, MEDIUM, LARGE, FULL } from '../../Atoms/ContentContainer';
import { ImageBoss } from '../../Atoms/ImageBoss';
import { BlockHeading } from '../../Atoms/BlockHeading';
import { sanitizeSvgOverlay } from './sanitizeSvgOverlay';
import styles from './ImageContent.module.scss';

interface Props {
  content: ContentType;
}
export function ImageContent(props: Props) {
  const { content } = props;
  const { getMediaItem, getImageCrop, translate } = useGetContentTranslations();
  const mediaItem = getMediaItem(content, 0);
  const imageCrop = getImageCrop(content, 0);
  const [showCaption, setShowCaption] = useState(false);
  // The overlay is injected via dangerouslySetInnerHTML after sanitizing with
  // (browser-only) DOMPurify, so it is rendered client-side after mount. SSR
  // emits the plain image — nothing unsanitized ships, and there is no jsdom on
  // the server. The mounted gate keeps the first client render matching SSR.
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => setIsMounted(true), []);

  if (!mediaItem) return null;
  const { url, alt_text, caption } = mediaItem;
  let size = LARGE;
  switch (content.metadata.size) {
    case 'full':
      size = FULL;
      break;
    case 'medium':
      size = MEDIUM;
      break;
    case 'small':
      size = SMALL;
      break;
    default:
      size = LARGE;
  }
  const isFull = size === FULL;
  const label = translate(content.translations, 'label');
  const secondaryText = translate(content.translations, 'description');

  const hasCaption = !!caption;

  // Super-admin/migration-authored <style>+<svg> overlay (e.g. converted
  // interactive image map). Rendered on top of the image inside the `.imgmap`
  // wrapper that the overlay's own CSS targets, only when explicitly enabled
  // and after mount (sanitization runs in the browser — see isMounted above).
  const adminConfig = content.metadata.admin_config;
  const svgOverlay =
    isMounted && adminConfig?.svg_overlay_enabled ? sanitizeSvgOverlay(adminConfig.svg_overlay ?? '') : '';

  return (
    <ContentContainer content={content}>
      <BlockHeading title={label} secondaryText={secondaryText} />
      {svgOverlay ? (
        <div className="imgmap">
          <ImageBoss
            url={url}
            alt={alt_text || ''}
            width={size}
            className={classNames(styles.image, isFull && styles.imageFull)}
            imageCrop={imageCrop}
          />
          <div dangerouslySetInnerHTML={{ __html: svgOverlay }} />
        </div>
      ) : (
        <ImageBoss
          url={url}
          alt={alt_text || ''}
          width={size}
          className={classNames(styles.image, isFull && styles.imageFull)}
          imageCrop={imageCrop}
        />
      )}
      {hasCaption && !showCaption && (
        <div
          tabIndex={0}
          role="button"
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              setShowCaption(true);
            }
          }}
          className={classNames(styles.caption, styles.showCaption)}
          onClick={() => setShowCaption(true)}
          aria-label={caption}
        >
          <Icon name="info" />
        </div>
      )}
      {hasCaption && showCaption && (
        <div
          tabIndex={0}
          role="button"
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              setShowCaption(false);
            }
          }}
          onClick={() => setShowCaption(false)}
          className={styles.caption}
        >
          {caption}
        </div>
      )}
    </ContentContainer>
  );
}
